<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import ConfirmRetryFailedCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmRetryFailedCampaignDialog.vue';
import CampaignDeliveryRecipientsDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignDeliveryRecipientsDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';

const { t } = useI18n();
const getters = useStoreGetters();
const store = useStore();

const selectedCampaign = ref(null);
const [showWhatsAppCampaignDialog, toggleWhatsAppCampaignDialog] = useToggle();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);
const confirmRetryFailedCampaignDialogRef = ref(null);
const deliveryRecipientsDialogRef = ref(null);

const WhatsAppCampaigns = computed(
  () => getters['campaigns/getWhatsAppCampaigns'].value
);

const hasNoWhatsAppCampaigns = computed(
  () => WhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const handleRetryFailed = campaign => {
  selectedCampaign.value = campaign;
  confirmRetryFailedCampaignDialogRef.value.dialogRef.open();
};

const handleViewRecipients = async campaign => {
  selectedCampaign.value = campaign;
  // The dialog fetches on open using its `selectedCampaign` prop; without
  // waiting for the prop to flush it would fetch the previously selected
  // campaign's recipients (release-audit finding C1).
  await nextTick();
  deliveryRecipientsDialogRef.value?.open();
};

// Syncing delivery history writes rows, so it must never run as a side effect
// of opening the page. It is an explicit, administrator-confirmed action.
const { isAdmin } = useAdmin();
const syncHistoryDialogRef = ref(null);
const isSyncingHistory = ref(false);
const refetchTimerId = ref(null);

const handleSyncHistoryConfirm = async () => {
  if (isSyncingHistory.value) return;
  isSyncingHistory.value = true;
  try {
    await store.dispatch('campaigns/syncDeliveryHistory');
    useAlert(t('CAMPAIGN.WHATSAPP.SYNC_HISTORY.API.SUCCESS_MESSAGE'));
    syncHistoryDialogRef.value?.close();
    refetchTimerId.value = window.setTimeout(
      () => store.dispatch('campaigns/get'),
      1500
    );
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.WHATSAPP.SYNC_HISTORY.API.ERROR_MESSAGE')
    );
  } finally {
    isSyncingHistory.value = false;
  }
};

onMounted(() => {
  store.dispatch('campaigns/get');
});

onBeforeUnmount(() => {
  if (refetchTimerId.value) window.clearTimeout(refetchTimerId.value);
});
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
    @click="toggleWhatsAppCampaignDialog()"
    @close="toggleWhatsAppCampaignDialog(false)"
  >
    <template #action>
      <WhatsAppCampaignDialog
        v-if="showWhatsAppCampaignDialog"
        @close="toggleWhatsAppCampaignDialog(false)"
      />
    </template>
    <div v-if="isAdmin" class="flex justify-end pb-3">
      <Button
        :label="t('CAMPAIGN.WHATSAPP.SYNC_HISTORY.BUTTON')"
        icon="i-lucide-refresh-cw"
        size="sm"
        color="slate"
        variant="faded"
        @click="syncHistoryDialogRef?.open()"
      />
    </div>
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoWhatsAppCampaigns"
      :campaigns="WhatsAppCampaigns"
      is-whats-app-type
      @delete="handleDelete"
      @retry-failed="handleRetryFailed"
      @view-recipients="handleViewRecipients"
    />
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <ConfirmRetryFailedCampaignDialog
      ref="confirmRetryFailedCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <CampaignDeliveryRecipientsDialog
      ref="deliveryRecipientsDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <Dialog
      ref="syncHistoryDialogRef"
      type="alert"
      :title="t('CAMPAIGN.WHATSAPP.SYNC_HISTORY.TITLE')"
      :description="t('CAMPAIGN.WHATSAPP.SYNC_HISTORY.DESCRIPTION')"
      :confirm-button-label="t('CAMPAIGN.WHATSAPP.SYNC_HISTORY.CONFIRM')"
      :is-loading="isSyncingHistory"
      :disable-confirm-button="isSyncingHistory"
      @confirm="handleSyncHistoryConfirm"
    />
  </CampaignLayout>
</template>
